import { useApplicationResourcesQuery } from "@graphql/application-resources/application-resources.g";
import { useClientEmployeeTotalCountQuery } from "@graphql/client-employees/clientEmployees.g";
import { NotificationEnum } from "@graphql/generated/graphql.g";
import { useUnresolvedNotificationsTotalCountQuery } from "@graphql/notifications/Notifications.g";
import c from "classnames";
import { useMemo, useState } from "react";
import { useRef } from "react";
import { useEffect } from "react";
import { Responsive } from "react-grid-layout";
import { Outlet, useNavigate } from "react-router";

import { TopUnresolvedNotificationsChart } from "@components/Charts";
import { PortalHeaderActions } from "@layouts/PortalPage/PortalHeaderActions";
import { TotalTenantsStat } from "@components/Stats";
import { ApplicationsUnreviewedTable } from "@components/Tables";

import { Button } from "@design-system/Button/Button";
import { Card } from "@design-system/Card/Card";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { StatCard } from "@design-system/StatCard/StatCard";

import { routes } from "@pages/routes";

import classes from "./MSPDashboard.module.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const layout = [
  { i: "f", x: 0, y: 0, w: 3, h: 4, isResizable: false },
  { i: "g", x: 3, y: 0, w: 3, h: 4, isResizable: false },
  { i: "h", x: 6, y: 0, w: 3, h: 4, isResizable: false },
  { i: "i", x: 9, y: 0, w: 3, h: 4, isResizable: false },

  { i: "c", x: 0, y: 4, w: 12, h: 24 },

  { i: "a", x: 0, y: 28, w: 12, h: 16 },
];

const layoutMd = [
  { i: "f", x: 0, y: 0, w: 6, h: 4, isResizable: false },
  { i: "g", x: 6, y: 0, w: 6, h: 4, isResizable: false },
  { i: "h", x: 0, y: 4, w: 6, h: 4, isResizable: false },
  { i: "i", x: 6, y: 4, w: 6, h: 4, isResizable: false },

  { i: "c", x: 0, y: 8, w: 12, h: 24 },

  { i: "a", x: 0, y: 32, w: 12, h: 16 },
];

const layoutSm = [
  { i: "f", x: 0, y: 0, w: 12, h: 4, isResizable: false },
  { i: "g", x: 0, y: 4, w: 12, h: 4, isResizable: false },
  { i: "h", x: 0, y: 8, w: 12, h: 4, isResizable: false },
  { i: "i", x: 0, y: 12, w: 12, h: 4, isResizable: false },

  { i: "c", x: 0, y: 16, w: 12, h: 28 },

  { i: "a", x: 0, y: 44, w: 12, h: 16 },
  { i: "b", x: 0, y: 60, w: 12, h: 16 },
];

const layouts = {
  lg: layout,
  md: layout,
  sm: layoutMd,
  xs: layoutMd,
  xxs: layoutSm,
};

export const MSPDashboard = () => {
  const navigate = useNavigate();
  const ref1 = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [dynamicLayouts, setDynamicLayouts] = useState(layouts);
  const [isEditingLayout, setIsEditingLayout] = useState(false);
  const layoutEditable = false;

  useEffect(() => {
    const handleResize = () => {
      if (ref1.current) {
        const offsetWidth = ref1.current.offsetWidth;
        setWidth(offsetWidth);

        if (offsetWidth >= 996) {
          const rect = ref1.current.getBoundingClientRect();
          // Calculate available height: window height minus top offset minus bottom padding (24px)
          const availableHeight = window.innerHeight - rect.top - 32;
          
          // Fixed rowHeight = 12, rowGap = 10
          const availableRows = Math.floor((availableHeight + 10) / 22);

          // Top cards are h: 4. Ensure we have at least min rows for the bottom cards
          const remainingRows = Math.max(20, availableRows - 4);
          
          // c (table) takes roughly 60% of remainingRows
          // a (chart) takes roughly 40% of remainingRows
          const cHeight = Math.max(12, Math.floor(remainingRows * 0.6));
          const aHeight = remainingRows - cHeight;
          
          setDynamicLayouts({
            ...layouts,
            lg: [
              ...layout.slice(0, 4), // f, g, h, i
              { ...layout[4], h: cHeight }, // c
              { ...layout[5], y: 4 + cHeight, h: aHeight }, // a
            ]
          });
        } else {
          setDynamicLayouts(layouts);
        }
      }
    };

    const observer = new ResizeObserver(handleResize);
    if (ref1.current) {
      observer.observe(ref1.current);
    }
    
    window.addEventListener("resize", handleResize);
    handleResize(); // initial set

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const totalClientCountQuery = useClientEmployeeTotalCountQuery();
  const isClientEmployeeCountLoading = totalClientCountQuery.isLoading;
  const employeeCount = useMemo(() => {
    return totalClientCountQuery.data?.clientEmployees?.totalCount || 0;
  }, [totalClientCountQuery.data]);

  // Fetch total count of primary SaaS application resources (discovered SaaS)
  const applicationResourcesQuery = useApplicationResourcesQuery({
    first: 1,
    offset: 0,
    isPrimaryResource: true,
    isSaas: true,
  });
  const isResourceGroupCountLoading = applicationResourcesQuery.isLoading;

  const resourceGroupCount = useMemo(() => {
    return applicationResourcesQuery.data?.applicationResources?.totalCount || 0;
  }, [applicationResourcesQuery.data]);

  const unresolvedNotificationCountQuery =
    useUnresolvedNotificationsTotalCountQuery({
      notificationType_In: [
        NotificationEnum.SharedLogin,
        NotificationEnum.SaasDiscovered,
        NotificationEnum.FileUpload,
      ],
    });
  const isNotificationCountLoading = unresolvedNotificationCountQuery.isLoading;
  const notificationCount = useMemo(() => {
    return (
      unresolvedNotificationCountQuery.data?.notifications?.totalCount || 0
    );
  }, [unresolvedNotificationCountQuery.data]);

  return (
    <>
      <PortalHeaderActions>
        {layoutEditable ? (
          !isEditingLayout ? (
            <Dropdown
              trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
            >
              <Dropdown.Option
                icon="faBorderNone"
                label="Edit layout"
                onSelect={() => setIsEditingLayout(true)}
              />
            </Dropdown>
          ) : (
            <Button
              icon="faPencil"
              onClick={() => setIsEditingLayout(false)}
              size="sm"
            >
              Exit edit mode
            </Button>
          )
        ) : null}
      </PortalHeaderActions>

      <div className={classes.container} ref={ref1}>
        <Responsive
          layouts={dynamicLayouts}
          rowHeight={12}
          width={width}
          cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
          isResizable={isEditingLayout}
          isDraggable={isEditingLayout}
          containerPadding={[0, 0]}
          autoSize
        >
          <div key="f" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <Clickable
                key={"client-list-employees"}
                onClick={() => {
                  navigate(routes.portal.clients.$path(), {
                    viewTransition: true,
                  });
                }}
                stretch
              >
                <StatCard
                  icon="faUser"
                  title="Users for all tenants"
                  isLoading={isClientEmployeeCountLoading}
                  value={employeeCount.toString()}
                />
              </Clickable>
            </div>
          </div>

          <div key="g" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <Clickable
                key={"client-list-apps"}
                onClick={() => {
                  navigate(routes.portal.clients.$path(), {
                    viewTransition: true,
                  });
                }}
                stretch
              >
                <StatCard
                  icon="faShapes"
                  title="Discovered SaaS"
                  isLoading={isResourceGroupCountLoading}
                  value={resourceGroupCount.toString()}
                />
              </Clickable>
            </div>
          </div>

          <div key="h" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <StatCard
                icon="faBell"
                title="Unresolved notifications"
                isLoading={isNotificationCountLoading}
                value={notificationCount.toString() || "0"}
              />
            </div>
          </div>

          <div key="i" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <Clickable
                key={"tenants-list"}
                onClick={() => {
                  navigate(routes.portal.clients.$path(), {
                    viewTransition: true,
                  });
                }}
                stretch
              >
                <TotalTenantsStat />
              </Clickable>
            </div>
          </div>

          <div key="c" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <Card grow>
                <Card.Header>
                  <Card.Header.Title>Applications to review</Card.Header.Title>
                </Card.Header>
                <Card.Body grow>
                  <ApplicationsUnreviewedTable search="" />
                </Card.Body>
              </Card>
            </div>
          </div>

          <div key="a" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <Card grow>
                <Card.Header>
                  <Card.Header.Title>
                    Most unresolved notifications
                  </Card.Header.Title>
                </Card.Header>
                <Card.Body grow>
                  <div className={classes.chart}>
                    <TopUnresolvedNotificationsChart />
                  </div>
                </Card.Body>
              </Card>
            </div>
          </div>
        </Responsive>
        <Outlet />
      </div>
    </>
  );
};
